/**
 * Google Analytics - Window dataLayer
 */
declare global {
    // eslint-disable-line @typescript-eslint/no-unused-vars
    interface Window {
        dataLayer?: Array<{ event: string; [key: string]: any }>;
    }
}

/**
 * Form Control
 */
export type FormControl = {
    name: string;
    key?: string;
    type: string;
    valueDelimiter?: string;
    conditionalLogic?: ConditionalLogic;
    inputs?: ControlInput[] | null;
    [key: string]: any;
};

export type ControlInput = {
    id: string;
    label?: string;
    isHidden?: boolean;
    defaultValue?: string;
    customLabel?: string;
    choices?: Array<iChoice>;
    isRequired?: boolean;
    isReadonly?: boolean;
};

/**
 * Form Validation Rule
 */
export type FormValidationRule = {
    (control: FormControl, value: any): [boolean, string];
};

export type FormValidationRuleWithState = {
    (
        control: FormControl,
        value: any,
        state: { controls: React.ReactNode[]; values: FormValues }
    ): [boolean, string];
};

/**
 * Form Validation Result
 */
export type FormValidationResult = {
    [name: string]: Array<string>;
};

/**
 * Form Values
 */
export type FormValues = {
    [name: string]: any;
};

export type ConditionalLogic = {
    actionType: "show" | "hide";
    logicType?: "all" | "any";
    rules: {
        name: string;
        operator: ConditionalLogicOperators;
        value: any;
    }[];
};

export type ConditionalLogicOperators =
    | ">"
    | "<"
    | "is"
    | "isnot"
    | "contains"
    | "starts_with"
    | "ends_width";

/**
 * FormSchema
 */
export type FormSchema = {
    description?: string;
    rules: (FormValidationRule | FormValidationRuleWithState)[];
    title: string;
    controls: FormControl[];
    button?: {
        text?: string;
        conditionalLogic?: ConditionalLogic;
        hide?: boolean;
    };
    defaultValues: FormValues;
    form_enable_first_page_sidebar?: boolean;
    form_enable_honeypot?: boolean;
    getControl: (id: number) => FormControl | undefined;
    getControlIndex: (id: number) => number | undefined;
    findControlsByType: (type: string) => FormControl[];
    // [key: string]: any;
};

export type FormState = {
    controls: React.ReactNode[];
    controlProps: { [key: string]: any };
    validation: FormValidationResult;
    values: FormValues;
    page: number;
    pagination: PaginatedComponents;
    rules: (FormValidationRule | FormValidationRuleWithState)[];
    visitedLastPage: boolean;
    response?: GravityResponse;
};

export type FormDispatchers = {
    dispatchAction: (action: FormAction) => void;
};

export interface FormValueBehaviours {
    getControl: (name: string) => FormControl | null;
    getValue: (name: string, defaultValue?: any) => any;
    getValues: () => FormValues;
    getFormData: () => FormData;
    isDirty: (name: string) => boolean;
    setValue: (name: string, value: any) => void;
    unsetValue: (name: string) => void;
    assignValues: (values: { [name: string]: any }) => void;
}

export interface ConditionalLogicBehaviours {
    isHidden: (name: string) => boolean;
    isVisible: (name: string) => boolean;
}

export interface PaginationBehaviours {
    getPage: (page?: number) => FormPage;
    paginateComponents: (components?: React.ReactNode[]) => PaginatedComponents;
    pageAfter: (page?: number) => number;
    pageBefore: (page?: number) => number;
    pageIsLast: (page?: number) => boolean;
    gotoPage: (page: number) => void;
    gotoNextPage: () => void;
    gotoLastPage: (skipValidation?: boolean) => void;
    gotoPreviousPage: () => void;
    isNextDisabled: (page?: number) => boolean;
}

export interface ValidationBehaviours {
    validate: (controls?: React.ReactNode[]) => FormValidationResult;
    validatePage: (page?: number) => FormValidationResult;
    isPageHidden: (page?: number) => boolean;
}

export type FormContext = FormValueBehaviours &
    ConditionalLogicBehaviours &
    PaginationBehaviours &
    ValidationBehaviours & {
        form: FormSchema;
        state: FormState & FormDispatchers;
    };

export type PaginatedElement = {
    type: any;
    key: string;
    props: FormControl;
    [key: string]: any;
};

export type PaginatedComponents = Array<PaginatedElement[]>;

export type FormBuilder = {
    addRules: (
        rules: (FormValidationRule | FormValidationRuleWithState)[]
    ) => void;
    appendControl: (control: FormControl) => number;
    insertControl: (index: number, control: FormControl) => number;
    prependControl: (control: FormControl) => number;
    updateControls: (updater: IControlMutator | IControlMutator[]) => void;
};

export interface iFormControlled {
    context?: FormContext;
}

export interface AccessibilitySetting {
    isShown: boolean;
    name: string;
    value: string;
}

export interface iControlProps {
    label?: string;
    description?: string;
    value?: any;
    tabIndex?: number;
    name?: string;
    onChange?: (value: any) => void;
    onBlur?: (value: any) => void;
    cssClass?: string;
    isRequired?: boolean;
    conditionalLogic?: ConditionalLogic;
    accessibilitySettings?: Array<AccessibilitySetting>;
}

export interface iChoice {
    text: string;
    value?: any;
    isSelected?: boolean;
    conditionalLogic?: ConditionalLogic;
    options?: { text: string; value: string }[];
    customProps?: { [key: string]: any };
}

export interface GFChoice {
    text: string;
    value: any;
    conditionalLogic?: GravityConditionalLogic;
}

/**
 * Form Control mutator interface
 */
export interface IControlMutator {
    (control: FormControl, index: number): FormControl;
}

/**
 * Form Paging
 */

export type FormPage = FormControl & {
    pageNumber: number;
    index: number;
    children: React.ReactElement[];
};

/**
 * Form Events
 */

export type FormEvents = {
    onChange?: (update: FormValues, context: FormContext) => void;
    onPage?: (page: number, context: FormContext) => void;
    onSubmit?: (submission: FormData, context: FormContext) => void;
    onError?: (error: Error, context: FormContext) => void;
};

/**
 * React component
 */
export type Component =
    | React.ComponentClass<any>
    | React.FunctionComponent<any>;

/**
 * Form Component
 */
export type FormComponentProps = {
    controls?: FormControl[];
    description?: string;
    rules?: (FormValidationRule | FormValidationRuleWithState)[];
    title?: string;
    defaultValues?: FormValues;
    page?: number;
    children?: React.ReactNode;
    inAccordion?: boolean;
    tempValues?: FormValues;
    setTempValues?: (values: { [key: string]: any }) => void;
    response?: GravityResponse;
    [key: string]: any;
} & FormEvents;
/**
 * Form component settings
 */
export interface iPageTemplate {
    (
        props: { [key: string]: any } & {
            context: FormContext;
            onSubmit?: () => void;
            children?: React.ReactNode;
        }
    ): React.ReactElement<any, any> | null;
}

export type FormComponentSettings = {
    pluginControls?: { [key: string]: any };
    controlPlacement?: "beforeChildren" | "afterChildren";
    templateComponent: iPageTemplate;
};

export type PagedFormComponentSettings = FormComponentSettings & {
    pagination?: { titles?: []; disabled?: boolean };
};

/** Gravity Forms */

export type GravityForm = {
    id: string | number;
    title?: string;
    description?: string;
    button?: GravityFormButton;
    fields: GravityField[];
    pagination?: { pages: string[] } | null;
    firstPageCssClass?: string | null;
    form_enable_first_page_sidebar?: boolean;
    form_enable_first_page_as_stage?: boolean;
    form_enable_honeypot?: boolean;
};

export type GravityFormButton = {
    text: string;
    conditionalLogic?: GravityConditionalLogic;
};

export type GravityField = {
    id: string | number;
    type: string;
    inputs?: ControlInput[] | null;
    [key: string]: any;
};

export type GravityConditionalLogic = {
    actionType: "show" | "hide";
    logicType?: "all" | "any";
    rules: {
        fieldId: string | number;
        operator: ConditionalLogicOperators;
        value: any;
    }[];
};

export type FormAction = {
    type: string;
    body: any;
};

export type GravityResponse = {
    isValid: boolean;
    validationMessages?: { [key: string]: FieldResponseError };
    confirmationMessage?: string;
};

export type FieldResponseError = {
    error: string;
    isEdited: boolean;
};

/**
 * Value Actions
 */

export enum ValueActionType {
    Assign = "assign",
    Set = "set",
    Unset = "unset"
}

export type ValueAssignAction = {
    type: ValueActionType.Assign;
    body: FormValues;
};

export type ValueSetAction = {
    type: ValueActionType.Set;
    body: {
        name: string;
        value: any;
    };
};

export type ValueUnsetAction = {
    type: ValueActionType.Unset;
    body: string;
};

export type ValueActions =
    | ValueAssignAction
    | ValueSetAction
    | ValueUnsetAction;

/**
 * Page Actions
 */

export enum PageActionType {
    GotoPage = "goto",
    GotoNext = "next",
    GotoPrevious = "previous"
}
